import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import QRCode from 'qrcode.react';
import { useGenerateQRCodeWithDownload } from '../../services/qrCodeHooks';
import RadioButtonsGroup from './RadioButtonsGroup';
import css from './QRCodeWidget.css';

// Logo option constants
const LOGO_OPTIONS = {
    NO_LOGO: 'no-logo',
    BRANDED_DEFAULT: 'branded-default',
    CUSTOM_LOGO: 'custom-logo',
};

function QRCodeWidget({ label = '', size = 115, value = '', defaultLogoSrc = '' }) {
    const [generateAndDownload] = useGenerateQRCodeWithDownload();
    const [logoOption, setLogoOption] = useState(LOGO_OPTIONS.NO_LOGO);
    const [customLogoSrc, setCustomLogoSrc] = useState('');
    const [isCustomLogoValid, setIsCustomLogoValid] = useState(false);
    const [isValidatingCustomLogo, setIsValidatingCustomLogo] = useState(false);

    // Update default logo option when logoSrc changes
    useEffect(() => {
        if (defaultLogoSrc) {
            setLogoOption(LOGO_OPTIONS.BRANDED_DEFAULT);
        } else {
            setLogoOption(LOGO_OPTIONS.NO_LOGO);
        }
    }, [defaultLogoSrc]);

    // Validate custom logo URL by trying to load it as an image
    const validateCustomLogo = useCallback(url => {
        if (!url.trim()) {
            setIsCustomLogoValid(false);
            setIsValidatingCustomLogo(false);
            return;
        }

        setIsValidatingCustomLogo(true);
        const img = new Image();

        img.onload = () => {
            setIsCustomLogoValid(true);
            setIsValidatingCustomLogo(false);
        };

        img.onerror = () => {
            setIsCustomLogoValid(false);
            setIsValidatingCustomLogo(false);
        };

        img.src = url;
    }, []);

    // Validate custom logo when URL changes
    useEffect(() => {
        if (customLogoSrc) {
            validateCustomLogo(customLogoSrc);
        } else {
            setIsCustomLogoValid(false);
            setIsValidatingCustomLogo(false);
        }
    }, [customLogoSrc, validateCustomLogo]);

    // Determine which logo to use for display and download
    const getEffectiveLogoSrc = useCallback(() => {
        if (logoOption === LOGO_OPTIONS.NO_LOGO) return '';
        if (logoOption === LOGO_OPTIONS.CUSTOM_LOGO && customLogoSrc && isCustomLogoValid) return customLogoSrc;
        if (logoOption === LOGO_OPTIONS.BRANDED_DEFAULT) return defaultLogoSrc;
        return '';
    }, [logoOption, customLogoSrc, isCustomLogoValid, defaultLogoSrc]);

    // Handle PNG/SVG download with appropriate logo
    const onDownloadClick = useCallback(
        type => {
            if (!value) return;

            // Validate custom logo if it's being used
            if (logoOption === LOGO_OPTIONS.CUSTOM_LOGO) {
                if (!customLogoSrc || !isCustomLogoValid) {
                    // Don't proceed with download if custom logo is invalid
                    return;
                }
            }

            let logoUrl = '';
            if (logoOption === LOGO_OPTIONS.BRANDED_DEFAULT) {
                logoUrl = defaultLogoSrc;
            } else if (logoOption === LOGO_OPTIONS.CUSTOM_LOGO && customLogoSrc) {
                logoUrl = customLogoSrc;
            }

            generateAndDownload({
                type,
                data: value,
                logoUrl,
                filename: 'QRCode',
            }).catch(error => {
                console.error('Error generating QR code:', error);
            });
        },
        [value, defaultLogoSrc, customLogoSrc, logoOption, isCustomLogoValid, generateAndDownload]
    );

    const handleCustomLogoChange = useCallback(e => {
        setCustomLogoSrc(e.target.value);
    }, []);

    const getRadioItems = useCallback(() => {
        const items = [
            {
                id: LOGO_OPTIONS.NO_LOGO,
                label: 'STATIC.QR_CODE.NO_LOGO',
                checked: logoOption === LOGO_OPTIONS.NO_LOGO,
            },
        ];

        if (defaultLogoSrc) {
            items.push({
                id: LOGO_OPTIONS.BRANDED_DEFAULT,
                label: 'STATIC.QR_CODE.BRANDED_LOGO',
                checked: logoOption === LOGO_OPTIONS.BRANDED_DEFAULT,
            });
        }

        items.push({
            id: LOGO_OPTIONS.CUSTOM_LOGO,
            label: 'STATIC.QR_CODE.CUSTOM_LOGO',
            checked: logoOption === LOGO_OPTIONS.CUSTOM_LOGO,
        });

        return items;
    }, [logoOption, defaultLogoSrc]);

    const handleRadioGroupChange = useCallback(item => {
        setLogoOption(item.id);
    }, []);

    const effectiveLogoSrc = getEffectiveLogoSrc();

    return (
        <div className={css.qrCodeContainer}>
            <div className={css.qrCodeTopArea}>
                <div className={css.qrCodeLabel}>
                    <Translate id={label} />
                </div>
            </div>
            <div className={css.qrCodeMainArea}>
                <div className={css.qrCodeBox}>
                    <div className={css.qrCodeInnerBox}>
                        {value && (
                            <>
                                <QRCode
                                    value={value}
                                    size={size}
                                    level="H"
                                    renderAs="canvas"
                                    style={{ width: '100%', height: '100%' }}
                                    {...(effectiveLogoSrc
                                        ? {
                                              imageSettings: {
                                                  src: effectiveLogoSrc,
                                                  height: size * 0.2,
                                                  width: size * 0.2,
                                                  excavate: true,
                                              },
                                          }
                                        : {})}
                                />
                            </>
                        )}
                    </div>
                </div>

                <div className={css.qrCodeControlsContainer}>
                    <div className={css.logoOptionsHeader}>
                        <span className={css.logoOptionsIcon}>🖼️</span>
                        <span className={css.text}>
                            <Translate id="STATIC.QR_CODE.LOGO_OPTIONS" />
                        </span>
                        <div className={css.downloadLinksContainer}>
                            <span className={css.text}>
                                <Translate id="STATIC.QR_CODE.DOWNLOAD" />
                            </span>
                            <a className={css.downloadLink} onClick={() => onDownloadClick('png')}>
                                <Translate id="STATIC.QR_CODE.DOWNLOAD_PNG" />
                            </a>
                            <a className={css.downloadLink} onClick={() => onDownloadClick('svg')}>
                                <Translate id="STATIC.QR_CODE.DOWNLOAD_SVG" />
                            </a>
                        </div>
                    </div>

                    <RadioButtonsGroup
                        radioItems={getRadioItems()}
                        onChange={handleRadioGroupChange}
                        className={css.radioGroup}
                        itemsClassName={css.radioOption}
                        checkmarkClassName={css.checkmark}
                    />

                    {logoOption === LOGO_OPTIONS.CUSTOM_LOGO && (
                        <div className={css.customLogoInputContainer}>
                            <input
                                type="text"
                                placeholder="https://example.com/logo.png"
                                value={customLogoSrc}
                                onChange={handleCustomLogoChange}
                                className={`${css.customLogoInput} ${
                                    customLogoSrc && !isValidatingCustomLogo
                                        ? isCustomLogoValid
                                            ? css.validInput
                                            : css.invalidInput
                                        : ''
                                }`}
                            />
                            <div className={css.customLogoDescription}>
                                <Translate id="STATIC.QR_CODE.CUSTOM_LOGO_LABEL" />
                            </div>
                            {isValidatingCustomLogo && (
                                <div className={css.validationMessage}>
                                    <Translate id="STATIC.QR_CODE.CUSTOM_LOGO_VALIDATION" />
                                </div>
                            )}
                            {customLogoSrc && !isValidatingCustomLogo && !isCustomLogoValid && (
                                <div className={css.errorMessage}>
                                    <Translate id="STATIC.QR_CODE.CUSTOM_LOGO_VALIDATION_FAILED" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

QRCodeWidget.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    size: PropTypes.number,
    defaultLogoSrc: PropTypes.string,
};

export default QRCodeWidget;
